import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiHome, FiPackage, FiMap, FiHeadphones, FiSettings, FiShoppingBag, FiCompass } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import { getInvestorHome, logoutInvestor, completeInvestorKyc, getPublicAvatars, changeInvestorPassword, getInvestorJourneys, getPublicInvestments, getPublicBoutiques, getPublicBoutiqueProducts, placeCartOrder, getMyOrders, updateThemePreference, updateNewsletterOptin, AvatarData, InvestorHomeResponse, Journey, PublicInvestment, PublicBoutique, PublicProduct, MyOrder } from '../api/portalApi';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { setTheme } from '../redux/slices/themeSlice';
import { PanelId, NavGroup, setActivePanel, setActiveGroup, setSelectedCargoId } from '../redux/slices/dashboardUiSlice';
import { CartItem, addToCart, removeFromCart, updateQuantity, clearCart } from '../redux/slices/cartSlice';
import type { DashboardTheme } from '../theme';
import { dashboardThemes } from '../theme';
import CargoMap from '../components/cargo/CargoMap';
import WorldMap from '../components/WorldMap';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import CurrencySwitcher from '../components/common/CurrencySwitcher';
import { useCurrency } from '../contexts/CurrencyContext';
import type { CurrencyCode } from '../contexts/CurrencyContext';
import WaitingRoom from '../components/WaitingRoom';
import BoardingScreen from '../components/BoardingScreen';
import MissionStatement from '../components/shop/MissionStatement';
import { track } from '../utils/analytics';
import { DM, SHOP_DM_VARS } from '../utils/shopTheme';
import '../styles/journeys.css';
import '../styles/boutique.css';

const SUPPORT_WHATSAPP = 'https://wa.me/+21622000000';

const LEGACY_AVATAR_MAP: Record<string, string> = {
  popeye: '/assets/popeyesmall.png',
  olive: '/assets/olive1.jpeg',
  curto: '/assets/cortomaltese.png',
};


function formatDate(value: string | null | undefined): string {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}


// Mobile nav — one entry per group + solo items
const mobileNavEntries: Array<{ panel: PanelId; group?: NavGroup; labelKey: string; Icon: IconType }> = [
  { panel: 'summary',  group: 'investor', labelKey: 'nav.investor',  Icon: FiPackage },
  { panel: 'shop',     group: 'shop',     labelKey: 'nav.shop',      Icon: FiShoppingBag },
  { panel: 'journeys', labelKey: 'nav.journeysShort', Icon: FiCompass },
  { panel: 'support',  labelKey: 'nav.supportShort',  Icon: FiHeadphones },
  { panel: 'settings', labelKey: 'nav.settingsShort', Icon: FiSettings },
];

const InvestorHome: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const dispatch = useAppDispatch();
  const activeTheme = useAppSelector((state: any) => state.theme.activeTheme);
  const activePanel = useAppSelector((state: any) => state.dashboardUi.activePanel);
  const activeGroup = useAppSelector((state: any) => state.dashboardUi.activeGroup) as NavGroup;
  const selectedCargoId = useAppSelector((state: any) => state.dashboardUi.selectedCargoId);
  const cartItems = useAppSelector((state: any) => state.cart.items) as CartItem[];

  const [data, setData] = useState<InvestorHomeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'me' | 'globe'>('me');
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [expandedCargos, setExpandedCargos] = useState<Set<string>>(new Set());

  // Avatar data
  const [avatarOptions, setAvatarOptions] = useState<AvatarData[]>([]);
  const [showSecretAvatar, setShowSecretAvatar] = useState(false);

  // Boutique marketplace
  const [boutiques, setBoutiques] = useState<PublicBoutique[]>([]);
  const [boutiquesLoaded, setBoutiquesLoaded] = useState(false);
  const [loadingBoutiques, setLoadingBoutiques] = useState(false);
  const [boutiquesError, setBoutiquesError] = useState(false);
  const [boutiqueFilter, setBoutiqueFilter] = useState<'all' | 'earth' | 'hands'>('all');

  // Journeys
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [journeysLoaded, setJourneysLoaded] = useState(false);
  const [loadingJourneys, setLoadingJourneys] = useState(false);

  // Portal unlock (WaitingRoom → full portal via Explore card)
  const [portalUnlocked, setPortalUnlocked] = useState(false);

  // Public investments browser (for WaitingRoom "Investment Lounge" card)
  const [pubInvestments, setPubInvestments] = useState<PublicInvestment[]>([]);
  const [pubInvestmentsLoaded, setPubInvestmentsLoaded] = useState(false);
  const [loadingPubInvestments, setLoadingPubInvestments] = useState(false);

  // Browse drilldown — inline boutique product browser
  const [browseBoutiqueId, setBrowseBoutiqueId] = useState<string | null>(null);
  const [browseBoutique, setBrowseBoutique] = useState<PublicBoutique | null>(null);
  const [boutiqueProducts, setBoutiqueProducts] = useState<PublicProduct[]>([]);
  const [loadingBoutiqueProducts, setLoadingBoutiqueProducts] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});

  // Cart checkout state
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutName, setCheckoutName] = useState('');
  const [checkoutMethod, setCheckoutMethod] = useState<'email' | 'whatsapp'>('email');
  const [checkoutDetail, setCheckoutDetail] = useState('');
  const [checkoutCountry, setCheckoutCountry] = useState('');
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  // Order history
  const [myOrders, setMyOrders] = useState<MyOrder[]>([]);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Settings panel state
  const [settingsName, setSettingsName] = useState('');
  const [settingsAvatar, setSettingsAvatar] = useState('');
  const [settingsCurrency, setSettingsCurrency] = useState('USD');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsError, setSettingsError] = useState('');

  // Password change state
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSaved, setPwSaved] = useState(false);

  // Newsletter state
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [newsletterSaving, setNewsletterSaving] = useState(false);
  const [newsletterSaved, setNewsletterSaved] = useState(false);

  const theme: DashboardTheme = dashboardThemes[activeTheme] ?? dashboardThemes[0]!;
  const { currency, format: formatMoney, convert: convertMoney, setCurrency } = useCurrency();

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const [response, avatarRes] = await Promise.all([getInvestorHome(), getPublicAvatars()]);
        if (isMounted) {
          setData(response);
          setError(null);
          const preferredCur = (response.investor.preferredCurrency || response.investor.currency || 'USD') as CurrencyCode;
          setCurrency(preferredCur);
          setAvatarOptions(avatarRes.avatars);
          setSettingsName(response.investor.displayName || response.investor.name || '');
          setSettingsCurrency(response.investor.preferredCurrency || response.investor.currency || 'USD');
          const av = response.investor.avatar || '';
          setSettingsAvatar(av);
          const found = avatarRes.avatars.find((a) => a._id === av);
          if (found?.secret) setShowSecretAvatar(true);
          setNewsletterOptIn(response.investor.newsletterOptIn === true);
          // preferredTheme (user's explicit pick) beats avatar default
          const themeToApply = response.investor.preferredTheme ?? found?.defaultTheme ?? 3;
          dispatch(setTheme(themeToApply));
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unable to load investor dashboard');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const firstCargoId = data?.cargos?.[0]?._id;
    if (firstCargoId && !selectedCargoId) {
      dispatch(setSelectedCargoId(firstCargoId));
    }
  }, [data, selectedCargoId, dispatch]);

  const handleLogout = async () => {
    track('investor-logout');
    await logoutInvestor();
    navigate('/');
  };

  const selectedCargo = useMemo(
    () => data?.cargos.find((item) => item._id === selectedCargoId) ?? data?.cargos[0] ?? null,
    [data, selectedCargoId]
  );

  const handlePanelChange = (panelId: PanelId) => {
    dispatch(setActivePanel(panelId));
    setCheckoutOpen(false);
    setCheckoutSuccess(false);
    track('investor-panel-switch', { panel: panelId });
  };

  const handleGroupClick = (group: NavGroup) => {
    dispatch(setActiveGroup(group));
    setCheckoutOpen(false);
    track('investor-group-switch', { group });
  };

  const handleBrowseBoutique = (boutique: PublicBoutique) => {
    setBrowseBoutiqueId(boutique._id);
    setBrowseBoutique(boutique);
    setBoutiqueProducts([]);
    setLoadingBoutiqueProducts(true);
    getPublicBoutiqueProducts(boutique._id)
      .then((r) => setBoutiqueProducts(r.products))
      .catch(() => {})
      .finally(() => setLoadingBoutiqueProducts(false));
    track('boutique_browse_open', { boutique: boutique.name });
  };

  const handleAddToCart = (product: PublicProduct, boutiqueName?: string) => {
    const variant = selectedVariants[product._id];
    const chosen = product.variants.find((v) => v.label === variant);
    const price = chosen ? chosen.price : product.price;
    dispatch(addToCart({
      productId: product._id,
      name: product.name,
      price,
      currency: product.currency,
      quantity: 1,
      variant: variant || undefined,
      imageUrl: product.coverImageUrl || product.images?.[0],
      boutiqueName,
      boutiqueId: product.boutiqueId,
    }));
    track('add_to_cart', { product: product.name });
  };

  const handleCartCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cartItems.length === 0) return;
    setCheckoutError('');
    setCheckoutLoading(true);
    try {
      await placeCartOrder({
        items: cartItems.map((i) => ({ productId: i.productId, variant: i.variant, quantity: i.quantity })),
        fullName: checkoutName.trim(),
        contactMethod: checkoutMethod,
        contactDetail: checkoutDetail.trim(),
        country: checkoutCountry.trim(),
        message: checkoutMessage.trim(),
      });
      dispatch(clearCart());
      setCheckoutSuccess(true);
      setCheckoutOpen(false);
      track('cart_checkout', { items: cartItems.length });
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'Order failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleThemeChange = (index: number) => {
    dispatch(setTheme(index));
    track('theme-change', { theme: dashboardThemes[index]?.name ?? index });
    setThemePickerOpen(false);
    void updateThemePreference(index);
  };

  // Track view mode switches and globe entry (covers all toggle entry points)
  useEffect(() => {
    track('investor-view-mode', { mode: viewMode });
    if (viewMode === 'globe') track('globe-open');
  }, [viewMode]);

  // Lazy-load shop products the first time the Shop panel is opened
  useEffect(() => {
    if (activePanel === 'shop' && !boutiquesLoaded) {
      track('shop-open');
      setLoadingBoutiques(true);
      setBoutiquesError(false);
      getPublicBoutiques()
        .then((r) => { setBoutiques(r.boutiques); setBoutiquesLoaded(true); })
        .catch(() => setBoutiquesError(true))
        .finally(() => setLoadingBoutiques(false));
    }
    if (activePanel === 'journeys' && !journeysLoaded) {
      setLoadingJourneys(true);
      getInvestorJourneys()
        .then((r) => { setJourneys(r.journeys); setJourneysLoaded(true); })
        .catch(() => {})
        .finally(() => setLoadingJourneys(false));
      track('journey_section_viewed', { context: 'investor' });
    }
    if (activePanel === 'open-investments' && !pubInvestmentsLoaded) {
      setLoadingPubInvestments(true);
      getPublicInvestments()
        .then((r) => { setPubInvestments(r.investments); setPubInvestmentsLoaded(true); })
        .catch(() => {})
        .finally(() => setLoadingPubInvestments(false));
      track('journey_section_viewed', { context: 'waiting-room-investments' });
    }
    if (activePanel === 'order-history' && !ordersLoaded) {
      setLoadingOrders(true);
      getMyOrders()
        .then((r) => { setMyOrders(r.orders); setOrdersLoaded(true); })
        .catch(() => {})
        .finally(() => setLoadingOrders(false));
    }
    if (activePanel !== 'shop') {
      setBrowseBoutiqueId(null);
      setBoutiqueProducts([]);
    }
  }, [activePanel, boutiquesLoaded, journeysLoaded, pubInvestmentsLoaded, ordersLoaded]);

  const renderSummary = () => {
    if (!data) return null;

    const investmentSourceCurrency = data.investor.currency || 'USD';
    const rawInvestment = data.investor.investmentAmount || 0;
    const investedConverted = convertMoney(rawInvestment, investmentSourceCurrency);
    const profitPct = data.investor.profitPercentageOnInvestment || 0;
    const expectedProfitConverted = Number(((investedConverted * profitPct) / 100).toFixed(0));
    const projectedPayoutConverted = investedConverted + expectedProfitConverted;

    return (
      <div className="summary-panel">
        <div className="dashboard-section hero-card hero-card--single" style={{ background: theme.surface, color: theme.text, boxShadow: `0 24px 70px ${theme.panelGlow}` }}>
          <div className="hero-card-main">
            <div className="hero-invested-amount" style={{ color: theme.accent }}>
              {formatMoney(rawInvestment, investmentSourceCurrency)}
            </div>
            <p className="hero-invested-label" style={{ color: theme.secondaryText }}>
              {t('summary.invested')}
            </p>
            <p className="hero-return-line" style={{ color: theme.secondaryText }}>
              ↑ {t('summary.expectedReturn')}: {formatMoney(projectedPayoutConverted, currency)} (+{profitPct}%)
            </p>
            <div className="hero-identity-row" style={{ borderTop: `1px solid rgba(255,255,255,0.07)` }}>
              {avatarBadgeSrc && (
                <img
                  src={avatarBadgeSrc}
                  alt={data.investor.displayName || data.investor.name}
                  style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${theme.accent}`, boxShadow: `0 0 0 2px ${theme.accent}33`, flexShrink: 0 }}
                />
              )}
              <span className="hero-identity-name" style={{ color: theme.text }}>
                {data.investor.displayName || data.investor.name}
              </span>
              <span className="hero-identity-meta" style={{ color: theme.secondaryText }}>
                · @{data.investor.username} · {data.cargos.length} cargo{data.cargos.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="dashboard-grid-stats">
          <div className="stat-card" style={{ background: theme.surface, color: theme.text }}>
            <span>{t('summary.invested')}</span>
            <strong>{formatMoney(rawInvestment, investmentSourceCurrency)}</strong>
          </div>
          <div className="stat-card" style={{ background: theme.surface, color: theme.text }}>
            <span>{t('summary.projectedProfit')}</span>
            <strong>{formatMoney(expectedProfitConverted, currency)}</strong>
          </div>
          <div className="stat-card" style={{ background: theme.surface, color: theme.text }}>
            <span>{t('summary.profitRate')}</span>
            <strong>{data.investor.profitPercentageOnInvestment ?? 0}%</strong>
          </div>
          <div className="stat-card" style={{ background: theme.surface, color: theme.text }}>
            <span>{t('summary.activeCargos')}</span>
            <strong>{data.cargos.length}</strong>
          </div>
          <div className="stat-card stat-card-wide" style={{ background: theme.surface, color: theme.text }}>
            <span>{t('summary.expectedPayout')}</span>
            <strong>{formatMoney(projectedPayoutConverted, currency)}</strong>
          </div>
        </div>
      </div>
    );
  };

  const renderCargos = () => {
    if (!data) return null;

    const liveStatuses = (data.investments ?? []).filter((inv) => inv.currentStatus.trim());

    return (
      <div className="cargo-panel" style={{ color: theme.text }}>
        {liveStatuses.length > 0 && (
          <div className="live-status-rail">
            {liveStatuses.map((inv) => (
              <div
                key={inv._id}
                className="live-status-card"
                style={{ background: theme.surface, borderColor: theme.accentSoft, ['--ls-accent' as string]: theme.accent }}
              >
                <span className="live-status-beacon">
                  <span className="live-status-ping" style={{ background: theme.accent }} />
                  <span className="live-status-dot" style={{ background: theme.accent }} />
                </span>
                <div className="live-status-content">
                  <span className="live-status-label" style={{ color: theme.accent }}>
                    {t('cargos.live')} · {inv.title}
                  </span>
                  <p className="live-status-text">{inv.currentStatus}</p>
                </div>
                <span className="live-status-emoji" aria-hidden="true">🛰️</span>
              </div>
            ))}
          </div>
        )}
        {data.cargos.length === 0 ? (
          <div className="cargo-empty-state">
            <div className="cargo-empty-mock" style={{ background: theme.surface, border: `1px dashed ${theme.accent}55`, opacity: 0.75 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>🚢</span>
                <span style={{ fontWeight: 700, color: theme.text, fontSize: '0.88rem' }}>{t('cargos.emptyMockTitle')}</span>
              </div>
              <p style={{ margin: 0, color: theme.secondaryText, fontSize: '0.74rem' }}>{t('cargos.emptyMockRoute')}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="cargo-empty-mock-bar" style={{ flex: 1, height: 8, background: `${theme.accent}22`, borderRadius: 6 }}>
                  <div style={{ width: '38%', height: '100%', background: theme.accent, borderRadius: 6 }} />
                </div>
                <span style={{ fontSize: '0.7rem', color: theme.accent, fontWeight: 700 }}>38%</span>
              </div>
              <span style={{ fontSize: '0.72rem', color: theme.secondaryText, fontStyle: 'italic' }}>{t('cargos.emptyMockStatus')}</span>
            </div>
            <p className="cargo-empty-title" style={{ color: theme.text }}>{t('cargos.noneTitle')}</p>
            <p className="cargo-empty-body" style={{ color: theme.secondaryText }}>{t('cargos.noneBody')}</p>
            <button
              type="button"
              className="cargo-empty-cta"
              style={{ background: theme.accent, color: theme.background }}
              onClick={() => dispatch(setActivePanel('open-investments'))}
            >
              {t('cargos.joinCta')}
            </button>
          </div>
        ) : (
          <div className="cargo-list">
            {data.cargos.map((cargo) => {
              const totalCost = (cargo.purchasePrice || 0) + (cargo.shippingPrice || 0) + (cargo.otherExpenses || 0);
              const isSelected = selectedCargo?._id === cargo._id;

              const createdMs = cargo.purchaseDate ? new Date(cargo.purchaseDate).getTime() : cargo.createdAt ? new Date(cargo.createdAt).getTime() : 0;
              const etaMs = new Date(cargo.estimatedTimeOfArrival).getTime();
              const nowMs = Date.now();
              const progress = createdMs > 0 && etaMs > createdMs
                ? Math.max(0, Math.min(1, (nowMs - createdMs) / (etaMs - createdMs)))
                : 0;
              const pct = Math.round(progress * 100);
              const daysAgo = createdMs > 0 ? Math.floor((nowMs - createdMs) / (1000 * 60 * 60 * 24)) : null;

              return (
                <div
                  key={cargo._id}
                  role="button"
                  tabIndex={0}
                  className={`cargo-card ${isSelected ? 'cargo-card-selected' : ''}`}
                  style={{
                    background: isSelected ? theme.accent : theme.surface,
                    color: isSelected ? theme.background : theme.text,
                  }}
                  onClick={() => {
                    dispatch(setSelectedCargoId(cargo._id));
                    track('cargo-open', { cargo: cargo.productBeingShipped });
                    setExpandedCargos((prev) => {
                      const next = new Set(prev);
                      if (next.has(cargo._id)) next.delete(cargo._id); else next.add(cargo._id);
                      return next;
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') dispatch(setSelectedCargoId(cargo._id));
                  }}
                >
                  {cargo.coverImageUrl && (
                    <img src={cargo.coverImageUrl} alt="" className="cargo-card-cover" />
                  )}
                  <div className="cargo-card-title">{cargo.productBeingShipped}</div>
                  <div className="cargo-card-meta">
                    {cargo.shippingType === 'air' ? '✈️' : cargo.shippingType === 'land' ? '🚛' : '🚢'}{' '}
                    {cargo.purchaseLocation} → {cargo.shippingDestination}
                  </div>
                  <div className="cargo-journey-progress">
                    <div className="cargo-journey-bar">
                      <div
                        className="cargo-journey-fill"
                        style={{ width: `${pct}%`, background: isSelected ? theme.background : theme.accent }}
                      />
                    </div>
                    <span className="cargo-journey-label" style={{ color: isSelected ? theme.background : theme.accent }}>
                      {pct}%
                    </span>
                  </div>
                  <button
                    type="button"
                    className="cargo-show-details"
                    style={{ color: isSelected ? theme.background : theme.secondaryText }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedCargos((prev) => {
                        const next = new Set(prev);
                        if (next.has(cargo._id)) next.delete(cargo._id); else next.add(cargo._id);
                        return next;
                      });
                    }}
                  >
                    {expandedCargos.has(cargo._id) ? 'Hide details ∧' : 'Show details ∨'}
                  </button>
                  <div className={`cargo-card-details${expandedCargos.has(cargo._id) ? ' cargo-card-details--open' : ''}`}>
                    {daysAgo !== null && (
                      <div className="cargo-card-narrative" style={{ color: isSelected ? theme.background : theme.secondaryText }}>
                        {t('cargos.narrative', {
                          type: cargo.shippingType ?? 'sea',
                          origin: cargo.purchaseLocation,
                          days: daysAgo,
                          pct,
                        })}
                      </div>
                    )}
                    <div className="cargo-card-footer">
                      {cargo.quantity} {t('cargos.units')} · {formatMoney(totalCost, cargo.currency)} {t('cargos.total')} · {t('cargos.eta')} {formatDate(cargo.estimatedTimeOfArrival)}
                    </div>
                  </div>
                  <div className="cargo-card-actions">
                    <button
                      type="button"
                      className="cargo-card-route-hint"
                      style={{
                        color: isSelected ? theme.background : theme.accent,
                        borderColor: isSelected ? theme.background : theme.accent,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(setSelectedCargoId(cargo._id));
                        dispatch(setActivePanel('map'));
                      }}
                    >
                      {t('cargos.viewRoute')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMap = () => {
    if (!selectedCargo) {
      return (
        <div className="map-panel" style={{ color: theme.text }}>
          <div className="empty-state" style={{ borderColor: theme.accent }}>
            <p>{t('map.selectPrompt')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="map-panel" style={{ color: theme.text }}>
        {/* Admin-set cargo description */}
        {selectedCargo.cargoDescription && (
          <div className="cargo-desc-card" style={{ background: theme.surface }}>
            <p className="cargo-desc-label">{t('map.about')}</p>
            <p className="cargo-desc-text">{selectedCargo.cargoDescription}</p>
          </div>
        )}

        {/* MapLibre map with journey animation */}
        <CargoMap
          cargos={data?.cargos ?? []}
          selectedCargoId={selectedCargo._id}
          onSelectCargo={(id) => dispatch(setSelectedCargoId(id))}
          theme={theme}
        />

        {/* Deal-sheet cost breakdown */}
        <div className="map-deal-sheet" style={{ background: theme.surface }}>
          <p className="map-deal-section-label" style={{ color: theme.accent }}>{t('map.sectionInvestment')}</p>
          <div className="map-deal-row">
            <span style={{ color: theme.secondaryText }}>{t('map.purchasePrice')}</span>
            <strong style={{ color: theme.text }}>{formatMoney(selectedCargo.purchasePrice, selectedCargo.currency)}</strong>
          </div>
          <div className="map-deal-row">
            <span style={{ color: theme.secondaryText }}>{t('map.otherFees')}</span>
            <strong style={{ color: theme.text }}>{formatMoney(selectedCargo.otherExpenses, selectedCargo.currency)}</strong>
          </div>
          <div className="map-deal-row map-deal-row--qty">
            <span style={{ color: theme.secondaryText }}>{t('map.quantity')}</span>
            <strong style={{ color: theme.text }}>{selectedCargo.quantity} {t('cargos.units')}</strong>
          </div>

          <p className="map-deal-section-label" style={{ color: theme.accent, marginTop: 16 }}>{t('map.sectionLogistics')}</p>
          <div className="map-deal-row">
            <span style={{ color: theme.secondaryText }}>{t('map.shippingCost')}</span>
            <strong style={{ color: theme.text }}>{formatMoney(selectedCargo.shippingPrice, selectedCargo.currency)}</strong>
          </div>

          <p className="map-deal-section-label" style={{ color: theme.accent, marginTop: 16 }}>{t('map.sectionTimeline')}</p>
          <div className="map-deal-row">
            <span style={{ color: theme.secondaryText }}>{t('map.eta')}</span>
            <strong style={{ color: theme.text }}>{formatDate(selectedCargo.estimatedTimeOfArrival)}</strong>
          </div>
          <div className="map-deal-row">
            <span style={{ color: theme.secondaryText }}>{t('map.sellWindow')}</span>
            <strong style={{ color: theme.text }}>{formatDate(selectedCargo.estimatedTimeOfSelling)}</strong>
          </div>
        </div>
      </div>
    );
  };

  const renderShop = (forceFullGrid = false) => {
    // Inline boutique product drilldown
    if (browseBoutiqueId && browseBoutique && !forceFullGrid) {
      const accent = browseBoutique.accentColor || DM.accent;
      return (
        <div style={{ color: DM.text, background: DM.background, borderRadius: 20, padding: 24, ...SHOP_DM_VARS }}>
          <button
            type="button"
            className="browse-back-btn"
            style={{ color: DM.textMuted, borderColor: `${DM.accent}33` }}
            onClick={() => { setBrowseBoutiqueId(null); setBrowseBoutique(null); setBoutiqueProducts([]); }}
          >
            ← Back to boutiques
          </button>
          <div className="browse-boutique-header">
            {browseBoutique.coverImageUrl && (
              <img src={browseBoutique.coverImageUrl} alt={browseBoutique.name} className="browse-boutique-cover" />
            )}
            <div className="browse-boutique-meta">
              {browseBoutique.logoUrl && <img src={browseBoutique.logoUrl} alt="" className="browse-boutique-logo" style={{ borderColor: accent }} />}
              <h2 style={{ color: DM.text, margin: 0 }}>{browseBoutique.name}</h2>
              {browseBoutique.location && <p style={{ color: DM.textMuted, margin: '4px 0 0', fontSize: '0.82rem' }}>📍 {browseBoutique.location}</p>}
              {browseBoutique.tagline && <p style={{ color: DM.textMuted, margin: '6px 0 0', fontSize: '0.86rem', fontStyle: 'italic' }}>{browseBoutique.tagline}</p>}
            </div>
          </div>

          {loadingBoutiqueProducts ? (
            <div className="browse-product-grid">
              {[1,2,3].map((i) => <div key={i} className="browse-product-skeleton" />)}
            </div>
          ) : boutiqueProducts.length === 0 ? (
            <div className="boutique-empty" style={{ marginTop: 32 }}>
              <div className="boutique-empty-icon">🏪</div>
              <p className="boutique-empty-text">No products yet in this boutique.</p>
            </div>
          ) : (
            <div className="browse-product-grid">
              {boutiqueProducts.map((product) => {
                const inCart = cartItems.some((i) => i.productId === product._id && (i.variant ?? '') === (selectedVariants[product._id] ?? ''));
                const selectedVariant = selectedVariants[product._id];
                const chosenVariant = product.variants.find((v) => v.label === selectedVariant);
                const displayPrice = chosenVariant ? chosenVariant.price : product.price;
                return (
                  <div key={product._id} className="browse-product-card" style={{ borderColor: `${accent}22` }}>
                    {product.coverImageUrl ? (
                      <img src={product.coverImageUrl} alt={product.name} className="browse-product-img" loading="lazy" />
                    ) : (
                      <div className="browse-product-img-empty" style={{ background: `${accent}18` }}>🛒</div>
                    )}
                    <div className="browse-product-body">
                      <p className="browse-product-name" style={{ color: DM.text }}>{product.name}</p>
                      {product.origin && <p className="browse-product-origin" style={{ color: DM.textMuted }}>📍 {product.origin}</p>}
                      <p className="browse-product-price" style={{ color: accent }}>
                        {displayPrice} {product.currency}
                      </p>
                      {product.variants.length > 0 && (
                        <div className="browse-variant-row">
                          {product.variants.map((v) => (
                            <button
                              key={v.label}
                              type="button"
                              className={`browse-variant-chip${selectedVariants[product._id] === v.label ? ' active' : ''}`}
                              style={selectedVariants[product._id] === v.label
                                ? { borderColor: accent, color: accent, background: `${accent}18` }
                                : { borderColor: `${DM.accent}33`, color: DM.textMuted }
                              }
                              onClick={() => setSelectedVariants((prev) => ({ ...prev, [product._id]: v.label }))}
                            >
                              {v.label}
                            </button>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className="browse-add-cart-btn"
                        style={inCart
                          ? { background: `${accent}22`, color: accent, borderColor: `${accent}55` }
                          : { background: accent, color: '#0a0c14' }
                        }
                        onClick={() => handleAddToCart(product, browseBoutique.name)}
                      >
                        {inCart ? '✓ In cart' : '+ Add to cart'}
                      </button>
                      <a
                        href={`/shop/boutique/${browseBoutique._id}/product/${product._id}`}
                        style={{ display: 'block', marginTop: 6, textAlign: 'center', fontSize: '0.73rem', color: DM.textMuted, textDecoration: 'none', opacity: 0.7 }}
                      >
                        View full page →
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const visible = boutiques.filter((b) => boutiqueFilter === 'all' || b.section === boutiqueFilter);
    return (
      <div style={{ color: DM.text, background: DM.background, borderRadius: 20, padding: 24, ...SHOP_DM_VARS }}>
        {/* Partner access banner */}
        <div className="shop-partner-header" style={{ borderColor: `${DM.accent}33`, background: `${DM.accent}0d` }}>
          <span className="shop-partner-badge" style={{ background: DM.accent, color: DM.background }}>
            {t('shop.partnerBadge')}
          </span>
          <p className="shop-partner-desc" style={{ color: DM.textMuted }}>
            {t('shop.partnerDesc')}
          </p>
        </div>

        {/* Filter bar */}
        <div className="boutique-filter-bar">
          {(['all', 'earth', 'hands'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`boutique-filter-tab${boutiqueFilter === s ? ' active' : ''}`}
              style={boutiqueFilter === s ? { borderColor: DM.accent, color: DM.accent } : { color: DM.textMuted }}
              onClick={() => setBoutiqueFilter(s)}
            >
              {s === 'all' ? 'All Boutiques' : s === 'earth' ? '🌍 From the Earth' : '🤲 From the Hands'}
            </button>
          ))}
        </div>

        <MissionStatement />

        {/* Grid */}
        <main className="boutique-grid">
          {loadingBoutiques && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="boutique-skeleton">
              <div className="boutique-skeleton-cover" />
              <div className="boutique-skeleton-body">
                <div className="boutique-skeleton-line" style={{ height: 12, width: '45%' }} />
                <div className="boutique-skeleton-line" style={{ height: 18, width: '75%' }} />
                <div className="boutique-skeleton-line" style={{ height: 12, width: '90%' }} />
              </div>
            </div>
          ))}

          {!loadingBoutiques && boutiquesError && (
            <div className="boutique-empty">
              <div className="boutique-empty-icon">⚠️</div>
              <p className="boutique-empty-text">{t('shopUi.loadError')}</p>
              <button
                type="button"
                onClick={() => { setBoutiquesError(false); setBoutiquesLoaded(false); }}
                style={{ marginTop: 16, padding: '8px 20px', borderRadius: 8, border: `1px solid ${DM.accent}55`, background: 'transparent', color: DM.accent, cursor: 'pointer', fontWeight: 700, fontSize: '0.84rem' }}
              >
                {t('shopUi.retry')}
              </button>
            </div>
          )}

          {!loadingBoutiques && !boutiquesError && visible.length === 0 && (
            <div className="boutique-empty">
              <div className="boutique-empty-icon">🏪</div>
              <p className="boutique-empty-text">
                {boutiqueFilter === 'all' ? 'No boutiques yet.' : 'No boutiques in this section yet.'}
              </p>
            </div>
          )}

          {!loadingBoutiques && !boutiquesError && visible.map((b) => (
            <div
              key={b._id}
              role="button"
              tabIndex={0}
              className="boutique-card"
              style={{ '--card-accent': b.accentColor ? `${b.accentColor}66` : `${DM.accent}66`, cursor: 'pointer' } as React.CSSProperties}
              onClick={() => handleBrowseBoutique(b)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleBrowseBoutique(b); }}
            >
              <div className="boutique-card-cover">
                {b.coverImageUrl
                  ? <img src={b.coverImageUrl} alt={b.name} loading="lazy" />
                  : <div className="boutique-card-cover-empty">{b.section === 'earth' ? '🌍' : '🤲'}</div>
                }
                {b.logoUrl && <img className="boutique-card-logo" src={b.logoUrl} alt="" />}
              </div>
              <div className="boutique-card-body">
                {b.location && <p className="boutique-card-location">{b.location}</p>}
                <h2 className="boutique-card-name" style={{ color: DM.text }}>{b.name}</h2>
                {b.tagline && <p className="boutique-card-tagline" style={{ color: DM.textMuted }}>{b.tagline}</p>}
                <div className="boutique-card-tags">
                  {b.category && <span className="boutique-tag">{b.category}</span>}
                  <span className="boutique-tag">{b.section === 'earth' ? 'From the Earth' : 'From the Hands'}</span>
                </div>
                <div className="boutique-card-footer">
                  <span className="boutique-card-count">
                    {b.productCount > 0 ? `${b.productCount} product${b.productCount !== 1 ? 's' : ''}` : 'Opening soon'}
                  </span>
                  <span className="boutique-card-cta" style={{ background: b.accentColor || DM.accent }}>
                    Browse →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </main>
      </div>
    );
  };

  const renderCart = () => {
    const totalCount = cartItems.reduce((s, i) => s + i.quantity, 0);

    // Group totals by currency
    const currencyTotals: Record<string, number> = {};
    for (const item of cartItems) {
      currencyTotals[item.currency] = (currencyTotals[item.currency] ?? 0) + item.price * item.quantity;
    }

    if (checkoutSuccess) {
      return (
        <div className="cart-panel" style={{ color: theme.text }}>
          <div className="cart-success-card" style={{ background: theme.surface, borderColor: `${theme.accent}44` }}>
            <div className="cart-success-icon">✅</div>
            <h2 style={{ color: theme.accent, margin: '0 0 8px' }}>Order placed!</h2>
            <p style={{ color: theme.secondaryText, margin: 0, fontSize: '0.9rem' }}>
              We received your order and will reach out within 24–48 hours to arrange everything.
            </p>
            <button
              type="button"
              className="cart-continue-btn"
              style={{ background: theme.accent, color: theme.background }}
              onClick={() => { setCheckoutSuccess(false); handlePanelChange('shop'); }}
            >
              Continue shopping
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="cart-panel" style={{ color: theme.text }}>
        <div className="cart-header">
          <h2 style={{ color: theme.text, margin: 0 }}>
            Cart {totalCount > 0 && <span className="cart-count-badge" style={{ background: theme.accent, color: theme.background }}>{totalCount}</span>}
          </h2>
        </div>

        {cartItems.length === 0 ? (
          <div className="cart-empty" style={{ borderColor: `${theme.accent}33` }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🛒</div>
            <p style={{ color: theme.secondaryText, margin: '0 0 20px', fontSize: '0.9rem' }}>Your cart is empty.</p>
            <button
              type="button"
              className="cart-continue-btn"
              style={{ background: theme.accent, color: theme.background }}
              onClick={() => handlePanelChange('shop')}
            >
              Browse boutiques
            </button>
          </div>
        ) : (
          <>
            <div className="cart-items">
              {cartItems.map((item) => (
                <div key={`${item.productId}-${item.variant ?? ''}`} className="cart-item" style={{ background: theme.surface, borderColor: `rgba(255,255,255,0.06)` }}>
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt={item.name} className="cart-item-img" />
                  )}
                  <div className="cart-item-info">
                    <p className="cart-item-name" style={{ color: theme.text }}>{item.name}</p>
                    {item.boutiqueName && <p className="cart-item-boutique" style={{ color: theme.secondaryText }}>{item.boutiqueName}</p>}
                    {item.variant && <p className="cart-item-variant" style={{ color: theme.accent }}>{item.variant}</p>}
                    <p className="cart-item-price" style={{ color: theme.secondaryText }}>
                      {item.price} {item.currency} each · <strong style={{ color: theme.text }}>{(item.price * item.quantity).toFixed(2)} {item.currency}</strong>
                    </p>
                  </div>
                  <div className="cart-item-controls">
                    <div className="cart-qty-ctrl" style={{ borderColor: `rgba(255,255,255,0.1)`, background: `rgba(255,255,255,0.04)` }}>
                      <button
                        type="button"
                        style={{ color: theme.secondaryText }}
                        onClick={() => dispatch(updateQuantity({ productId: item.productId, variant: item.variant, quantity: item.quantity - 1 }))}
                        disabled={item.quantity <= 1}
                      >−</button>
                      <span style={{ color: theme.text }}>{item.quantity}</span>
                      <button
                        type="button"
                        style={{ color: theme.secondaryText }}
                        onClick={() => dispatch(updateQuantity({ productId: item.productId, variant: item.variant, quantity: item.quantity + 1 }))}
                      >+</button>
                    </div>
                    <button
                      type="button"
                      className="cart-remove-btn"
                      style={{ color: theme.secondaryText }}
                      onClick={() => dispatch(removeFromCart({ productId: item.productId, variant: item.variant }))}
                      aria-label="Remove"
                    >✕</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-totals" style={{ borderColor: `rgba(255,255,255,0.08)` }}>
              {Object.entries(currencyTotals).map(([cur, total]) => (
                <div key={cur} className="cart-total-row">
                  <span style={{ color: theme.secondaryText }}>Total ({cur})</span>
                  <strong style={{ color: theme.accent, fontSize: '1.1rem' }}>{total.toFixed(2)} {cur}</strong>
                </div>
              ))}
            </div>

            {!checkoutOpen ? (
              <button
                type="button"
                className="cart-checkout-trigger"
                style={{ background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentSoft})`, color: theme.background }}
                onClick={() => setCheckoutOpen(true)}
              >
                Proceed to checkout →
              </button>
            ) : (
              <form className="cart-checkout-form" onSubmit={handleCartCheckout} style={{ borderColor: `${theme.accent}22`, background: `${theme.accent}07` }}>
                <p className="cart-checkout-title" style={{ color: theme.accent }}>Checkout</p>

                <label className="cart-field">
                  <span style={{ color: theme.secondaryText }}>Full name</span>
                  <input
                    value={checkoutName}
                    onChange={(e) => setCheckoutName(e.target.value)}
                    placeholder="Your name"
                    required
                    style={{ background: theme.surface, color: theme.text, borderColor: `rgba(255,255,255,0.1)` }}
                  />
                </label>

                <div className="cart-field">
                  <span style={{ color: theme.secondaryText }}>Contact via</span>
                  <div className="cart-method-toggle">
                    {(['email', 'whatsapp'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`cart-method-btn${checkoutMethod === m ? ' active' : ''}`}
                        style={checkoutMethod === m
                          ? { borderColor: theme.accent, color: theme.accent, background: `${theme.accent}18` }
                          : { borderColor: `rgba(255,255,255,0.12)`, color: theme.secondaryText }
                        }
                        onClick={() => setCheckoutMethod(m)}
                      >
                        {m === 'email' ? '✉️ Email' : '💬 WhatsApp'}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="cart-field">
                  <span style={{ color: theme.secondaryText }}>{checkoutMethod === 'email' ? 'Email address' : 'WhatsApp number'}</span>
                  <input
                    value={checkoutDetail}
                    onChange={(e) => setCheckoutDetail(e.target.value)}
                    placeholder={checkoutMethod === 'email' ? 'you@example.com' : '+1 234 567 8900'}
                    required
                    type={checkoutMethod === 'email' ? 'email' : 'tel'}
                    style={{ background: theme.surface, color: theme.text, borderColor: `rgba(255,255,255,0.1)` }}
                  />
                </label>

                <label className="cart-field">
                  <span style={{ color: theme.secondaryText }}>Country</span>
                  <input
                    value={checkoutCountry}
                    onChange={(e) => setCheckoutCountry(e.target.value)}
                    placeholder="Your country"
                    style={{ background: theme.surface, color: theme.text, borderColor: `rgba(255,255,255,0.1)` }}
                  />
                </label>

                <label className="cart-field">
                  <span style={{ color: theme.secondaryText }}>Message (optional)</span>
                  <textarea
                    value={checkoutMessage}
                    onChange={(e) => setCheckoutMessage(e.target.value)}
                    placeholder="Special requests, delivery notes…"
                    rows={3}
                    style={{ background: theme.surface, color: theme.text, borderColor: `rgba(255,255,255,0.1)` }}
                  />
                </label>

                {checkoutError && <p className="cart-error">{checkoutError}</p>}

                <div className="cart-checkout-actions">
                  <button
                    type="button"
                    className="cart-cancel-btn"
                    style={{ color: theme.secondaryText, borderColor: `rgba(255,255,255,0.12)` }}
                    onClick={() => setCheckoutOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="cart-place-btn"
                    style={{ background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentSoft})`, color: theme.background }}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Placing order…' : 'Place order'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    );
  };

  const renderOrderHistory = () => {
    const STATUS_LABEL: Record<string, string> = { new: 'Processing', read: 'Confirmed', contacted: 'Delivered' };
    const STATUS_COLOR: Record<string, string> = { new: '#fbbf24', read: '#38bdf8', contacted: '#22c55e' };

    return (
      <div className="history-panel" style={{ color: theme.text }}>
        <h2 style={{ color: theme.text, margin: '0 0 20px', fontSize: '1.2rem', fontWeight: 800 }}>Order History</h2>

        {loadingOrders ? (
          <div className="history-skeleton-list">
            {[1,2,3].map((i) => <div key={i} className="history-skeleton" style={{ background: theme.surface }} />)}
          </div>
        ) : myOrders.length === 0 ? (
          <div className="cart-empty" style={{ borderColor: `${theme.accent}33` }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📋</div>
            <p style={{ color: theme.secondaryText, margin: '0 0 20px', fontSize: '0.9rem' }}>No orders yet.</p>
            <button
              type="button"
              className="cart-continue-btn"
              style={{ background: theme.accent, color: theme.background }}
              onClick={() => handlePanelChange('shop')}
            >
              Start shopping
            </button>
          </div>
        ) : (
          <div className="history-list">
            {myOrders.map((order) => (
              <div key={order._id} className="history-item" style={{ background: theme.surface, borderColor: `rgba(255,255,255,0.06)` }}>
                <div className="history-item-main">
                  <p className="history-item-name" style={{ color: theme.text }}>{order.productName}</p>
                  {order.boutiqueName && <p className="history-item-boutique" style={{ color: theme.secondaryText }}>{order.boutiqueName}</p>}
                  {order.variant && <p className="history-item-variant" style={{ color: theme.accent, fontSize: '0.78rem' }}>{order.variant}</p>}
                  <p className="history-item-meta" style={{ color: theme.secondaryText }}>
                    {order.quantity} × {order.unitPrice} {order.currency}
                    {' · '}
                    <strong style={{ color: theme.text }}>{order.total} {order.currency}</strong>
                    {' · '}
                    {new Date(order.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <span
                  className="history-item-status"
                  style={{ color: STATUS_COLOR[order.status] ?? theme.accent, background: `${STATUS_COLOR[order.status] ?? theme.accent}18`, borderColor: `${STATUS_COLOR[order.status] ?? theme.accent}44` }}
                >
                  {STATUS_LABEL[order.status] ?? order.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderJourneys = () => {
    const CLR = theme.accent;
    return (
      <div className="journeys-investor-panel" style={{ color: theme.text }}>
        {/* Partner access banner */}
        <div className="journeys-partner-banner" style={{ background: `${CLR}11`, borderColor: `${CLR}33` }}>
          <span className="journeys-partner-badge" style={{ color: CLR, borderColor: `${CLR}55` }}>{t('shop.partnerBadge')}</span>
          <p className="journeys-partner-desc" style={{ color: theme.secondaryText }}>{t('journeys.partnerDesc')}</p>
        </div>

        {loadingJourneys ? (
          <div style={{ display: 'flex', gap: 16 }}>
            {[1,2].map((i) => <div key={i} style={{ flex: 1, height: 260, borderRadius: 18, background: theme.surface, opacity: 0.5 }} />)}
          </div>
        ) : journeys.filter((j) => j.status !== 'past').length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', opacity: 0.5 }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>🧭</div>
            <p style={{ color: theme.secondaryText, fontSize: '0.86rem' }}>{t('journeys.comingSoon')}</p>
          </div>
        ) : (
          <div className="journeys-grid">
            {journeys.filter((j) => j.status !== 'past').map((j) => {
              const minPrice = j.durations.length > 0 ? Math.min(...j.durations.map((d) => d.price)) : null;
              const minCurrency = j.durations[0]?.currency ?? 'USD';
              return (
                <div
                  key={j._id}
                  className="journey-card"
                  style={{ background: theme.surface, borderColor: `${CLR}22`, cursor: 'pointer' }}
                  onClick={() => { navigate(`/journeys/${j._id}`); track('journey_card_clicked', { journeyId: j._id, context: 'investor' }); }}
                >
                  <div className="journey-card-cover">
                    {j.coverImageUrl ? (
                      <img src={j.coverImageUrl} alt={j.title} loading="lazy" />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: `${CLR}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', opacity: 0.5 }}>🌍</div>
                    )}
                    <div className="journey-card-cover-overlay" />
                    <span className={`journey-spots-badge${j.status === 'full' ? ' journey-full-badge' : ''}`} style={{ color: j.status === 'full' ? undefined : CLR }}>
                      {j.status === 'draft' ? '🔒 ' + t('journeys.partnerPreview') : j.status === 'full' ? t('journeys.full') : `${j.spotsRemaining} ${t('journeys.spotsLeft')}`}
                    </span>
                  </div>
                  <div className="journey-card-body">
                    <span className="journey-card-location" style={{ color: CLR }}>📍 {j.location}</span>
                    <h3 className="journey-card-title" style={{ color: theme.text }}>{j.title}</h3>
                    <p className="journey-card-excerpt" style={{ color: theme.secondaryText }}>{j.tagline || j.story.slice(0, 140)}</p>
                    <div className="journey-card-durations">
                      {j.durations.map((d, i) => (
                        <span key={i} className="journey-duration-chip" style={{ borderColor: `${CLR}33`, color: theme.secondaryText }}>{d.label}</span>
                      ))}
                    </div>
                    <div className="journey-card-footer">
                      {minPrice != null ? (
                        <span className="journey-card-price" style={{ color: theme.secondaryText }}>
                          {t('journeys.from')} <strong style={{ color: theme.text }}>{formatMoney(minPrice, minCurrency)}</strong>
                        </span>
                      ) : <span />}
                      <span className="journey-card-cta" style={{ background: CLR, color: '#000' }}>{t('journeys.learnMore')}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderOpenInvestments = () => {
    return (
      <div className="landing-section-inner" style={{ color: theme.text }}>
        <p style={{ textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 700, fontSize: '0.65rem', color: theme.accent, marginBottom: 8 }}>
          Open Opportunities
        </p>
        <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 800, margin: '0 0 8px', color: theme.text }}>
          Investment Lounge
        </h2>
        <p style={{ color: theme.secondaryText, fontSize: '0.88rem', margin: '0 0 36px' }}>
          Browse open investment opportunities. Once your profile is reviewed, you'll receive a personal invitation.
        </p>

        {loadingPubInvestments ? (
          <div className="investment-card-grid">
            <div className="investment-card-skeleton" />
            <div className="investment-card-skeleton" />
            <div className="investment-card-skeleton" />
          </div>
        ) : pubInvestments.length === 0 ? (
          <p style={{ color: theme.secondaryText, textAlign: 'center', marginTop: 60, fontSize: '0.9rem' }}>
            No open investments at the moment. Check back soon.
          </p>
        ) : (
          <div className="investment-card-grid">
            {pubInvestments.map((inv) => {
              const stColor = inv.status === 'active' ? '#22c55e' : inv.status === 'in_progress' ? '#38bdf8' : '#fbbf24';
              const stBg    = inv.status === 'active' ? 'rgba(34,197,94,0.1)' : inv.status === 'in_progress' ? 'rgba(56,189,248,0.1)' : 'rgba(251,191,36,0.1)';
              return (
                <div key={inv._id} className="investment-card" style={{ background: theme.surface, borderColor: `${theme.accent}22` }}>
                  {inv.coverImageUrl && (
                    <img src={inv.coverImageUrl} alt="" className="investment-card-cover" />
                  )}
                  <div className="investment-card-header">
                    <h3 className="investment-card-title" style={{ color: theme.text }}>{inv.title}</h3>
                    <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, color: stColor, background: stBg, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {inv.status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="investment-card-desc" style={{ color: theme.secondaryText }}>{inv.description}</p>
                  <div className="investment-card-tags">
                    <span className="investment-card-tag" style={{ color: theme.secondaryText, borderColor: `${theme.accent}33` }}>
                      Min {formatMoney(inv.minimumInvestment, inv.currency)}
                    </span>
                    <span className="investment-card-tag" style={{ color: theme.secondaryText, borderColor: `${theme.accent}33` }}>
                      {inv.cargoCount} cargo{inv.cargoCount !== 1 ? 's' : ''}
                    </span>
                    <span className="investment-card-tag" style={{ color: theme.secondaryText, borderColor: `${theme.accent}33` }}>
                      {inv.investorCount} investor{inv.investorCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="investment-card-join"
                    style={{ background: theme.accent }}
                    onClick={() => { track('join-click', { investmentId: inv._id, context: 'waiting-room' }); navigate(`/join/${inv._id}`); }}
                  >
                    Request to Join →
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderSupport = () => {
    return (
      <div className="support-panel" style={{ color: theme.text }}>
        <div className="support-card" style={{ background: theme.surface }}>
          <h2 style={{ margin: '0 0 6px', fontSize: '1.4rem', fontWeight: 800 }}>
            {t('support.needHelp')}
          </h2>
          <p style={{ margin: '0 0 20px', color: theme.secondaryText, fontSize: '0.88rem', lineHeight: 1.5 }}>
            {t('support.sendMessage')}
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <a
              href={SUPPORT_WHATSAPP}
              target="_blank"
              rel="noreferrer"
              className="support-button support-button--whatsapp"
            >
              📱 {t('support.whatsapp')}
            </a>
            <a
              href={`mailto:contact@nomadme.life?subject=Support%20%E2%80%94%20%40${data?.investor.username ?? ''}`}
              target="_blank"
              rel="noreferrer"
              className="support-button"
              style={{ background: theme.accent, color: theme.background }}
            >
              {t('support.contactSupport')}
            </a>
          </div>
        </div>
        <div className="support-faq" style={{ background: theme.surface }}>
          <p className="support-faq-title" style={{ color: theme.secondaryText }}>{t('support.faqTitle')}</p>
          {(Array.isArray(t('support.faqs', { returnObjects: true })) ? t('support.faqs', { returnObjects: true }) as Array<{ q: string; a: string }> : []).map((faq, i) => (
            <details key={i} className="support-faq-item">
              <summary className="support-faq-q" style={{ color: theme.text }}>{faq.q}</summary>
              <p className="support-faq-a" style={{ color: theme.secondaryText }}>{faq.a}</p>
            </details>
          ))}
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    const handleSave = async (e: React.FormEvent) => {
      e.preventDefault();
      setSettingsSaving(true);
      setSettingsError('');
      setSettingsSaved(false);
      try {
        const updatedInvestor = await completeInvestorKyc({
          avatar: settingsAvatar,
          displayName: settingsName.trim() || data?.investor.name || 'Investor',
          preferredCurrency: settingsCurrency,
        });
        setData((prev) => prev ? { ...prev, investor: { ...prev.investor, ...updatedInvestor } } : prev);
        track('investor-settings-save', { currency: settingsCurrency });
        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
      } catch (err) {
        setSettingsError(err instanceof Error ? err.message : t('settings.pwFailed'));
      } finally {
        setSettingsSaving(false);
      }
    };

    const currentAvatarSrc = avatarBadgeMap[settingsAvatar] ?? '/logos/nomadme_sourcing_trading_mark.png';
    const visibleAvatars = avatarOptions.filter((a) => !a.secret || showSecretAvatar);
    const hasSecrets = avatarOptions.some((a) => a.secret);

    const handlePasswordChange = async (e: React.FormEvent) => {
      e.preventDefault();
      setPwError('');
      if (pwNew !== pwConfirm) { setPwError(t('settings.pwMismatch')); return; }
      if (pwNew.length < 6) { setPwError(t('settings.pwTooShort')); return; }
      setPwSaving(true);
      try {
        await changeInvestorPassword({ currentPassword: pwCurrent, newPassword: pwNew });
        track('investor-password-change');
        setPwSaved(true);
        setPwCurrent(''); setPwNew(''); setPwConfirm('');
        setTimeout(() => setPwSaved(false), 3000);
      } catch (err) {
        setPwError(err instanceof Error ? err.message : t('settings.pwFailed'));
      } finally {
        setPwSaving(false);
      }
    };

    return (
      <div className="story-panel" style={{ color: theme.text }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 24, alignItems: 'start' }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '1.2rem', fontWeight: 800 }}>{t('settings.title')}</h2>
            <p style={{ margin: '0 0 24px', color: theme.secondaryText, fontSize: '0.85rem' }}>
              {t('settings.subtitle')}
            </p>

            <p className="settings-section-header" style={{ color: theme.accent }}>{t('settings.sectionAppearance')}</p>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Display name */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.secondaryText }}>{t('settings.displayName')}</span>
                <input
                  value={settingsName}
                  onChange={(e) => setSettingsName(e.target.value)}
                  placeholder={t('settings.displayNamePlaceholder')}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: `1px solid ${theme.accentSoft}`,
                    background: theme.surface,
                    color: theme.text,
                    fontSize: '0.95rem',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              </label>

              {/* Display currency */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.secondaryText }}>{t('settings.displayCurrency')}</span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                  {([
                    { code: 'USD', name: 'US Dollar' },
                    { code: 'EUR', name: 'Euro' },
                    { code: 'TND', name: 'Tunisian Dinar' },
                    { code: 'CNY', name: 'Chinese Yuan' },
                    { code: 'CFA', name: 'West African Franc' },
                  ] as const).map(({ code, name }) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => { setSettingsCurrency(code); setCurrency(code as CurrencyCode); }}
                      style={{
                        padding: '10px 6px',
                        borderRadius: 10,
                        border: settingsCurrency === code ? `2px solid ${theme.accent}` : `1px solid rgba(255,255,255,0.1)`,
                        background: settingsCurrency === code ? `${theme.accent}22` : theme.surface,
                        color: settingsCurrency === code ? theme.accent : theme.secondaryText,
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <span>{code}</span>
                      <span style={{ fontSize: '0.55rem', fontWeight: 500, opacity: 0.55, lineHeight: 1.2 }}>{name}</span>
                    </button>
                  ))}
                </div>
                <p style={{ margin: 0, fontSize: '0.72rem', color: theme.secondaryText, opacity: 0.7 }}>
                  {t('settings.currencyHint', { currency: settingsCurrency })}
                </p>
              </label>

              {/* Avatar picker — dynamic */}
              <div>
                <p style={{ margin: '0 0 10px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.secondaryText }}>
                  {t('settings.chooseAvatar')}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                  {visibleAvatars.map((av) => (
                    <button
                      key={av._id}
                      type="button"
                      onClick={() => { setSettingsAvatar(av._id); dispatch(setTheme(av.defaultTheme ?? 0)); track('investor-avatar-select', { avatar: av.name }); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 16,
                        border: settingsAvatar === av._id ? `2px solid ${theme.accent}` : `1px solid rgba(255,255,255,0.1)`,
                        background: settingsAvatar === av._id ? `${theme.accent}22` : theme.surface,
                        color: theme.text, cursor: 'pointer', textAlign: 'left',
                        gridColumn: av.secret ? '1 / -1' : undefined, transition: 'all 0.18s',
                      }}
                    >
                      <img src={av.imageUrl} alt={av.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${settingsAvatar === av._id ? theme.accent : 'transparent'}` }} />
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{av.name}</span>
                        {av.secret && <span style={{ fontSize: '0.68rem', color: theme.secondaryText }}>{t('settings.secretAvatar')}</span>}
                      </span>
                      {settingsAvatar === av._id && <span style={{ marginLeft: 'auto', color: theme.accent, fontSize: '1rem' }}>✓</span>}
                    </button>
                  ))}
                  {hasSecrets && !showSecretAvatar && (
                    <button type="button" onClick={() => setShowSecretAvatar(true)}
                      style={{ padding: '10px 12px', borderRadius: 16, border: '1px dashed rgba(255,255,255,0.2)', background: 'transparent', color: theme.secondaryText, cursor: 'pointer', fontSize: '0.82rem', gridColumn: '1 / -1' }}>
                      {t('settings.revealSecret')}
                    </button>
                  )}
                </div>
              </div>

              {settingsError && (
                <p style={{ margin: 0, color: '#f87171', fontSize: '0.82rem' }}>{settingsError}</p>
              )}

              <button
                type="submit"
                disabled={settingsSaving}
                style={{
                  padding: '13px',
                  borderRadius: 12,
                  border: 'none',
                  background: settingsSaved ? '#22c55e' : `linear-gradient(90deg, ${theme.accent}, ${theme.accentSoft})`,
                  color: '#000',
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  cursor: settingsSaving ? 'wait' : 'pointer',
                  transition: 'background 0.3s',
                }}
              >
                {settingsSaving ? t('settings.saving') : settingsSaved ? t('settings.saved') : t('settings.save')}
              </button>
            </form>

            {/* Newsletter */}
            <p className="settings-section-header" style={{ color: theme.accent, marginTop: 28, paddingTop: 20, borderTop: `1px solid rgba(255,255,255,0.08)` }}>Newsletter</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 16px', borderRadius: 14, background: theme.surface }}>
              <div>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '0.88rem', color: theme.text }}>NomadMe updates</p>
                <p style={{ margin: '3px 0 0', fontSize: '0.75rem', color: theme.secondaryText, lineHeight: 1.5 }}>Occasional news about opportunities, journeys, and product drops.</p>
              </div>
              <button
                type="button"
                disabled={newsletterSaving}
                onClick={async () => {
                  const next = !newsletterOptIn;
                  setNewsletterSaving(true);
                  setNewsletterSaved(false);
                  try {
                    await updateNewsletterOptin(next);
                    setNewsletterOptIn(next);
                    setNewsletterSaved(true);
                    setTimeout(() => setNewsletterSaved(false), 2000);
                  } catch { /* silent */ } finally {
                    setNewsletterSaving(false);
                  }
                }}
                style={{
                  flexShrink: 0,
                  width: 48,
                  height: 28,
                  borderRadius: 14,
                  border: 'none',
                  background: newsletterOptIn ? theme.accent : 'rgba(255,255,255,0.12)',
                  cursor: newsletterSaving ? 'wait' : 'pointer',
                  position: 'relative',
                  transition: 'background 0.25s',
                }}
                aria-label="Toggle newsletter"
              >
                <span style={{
                  position: 'absolute',
                  top: 3,
                  left: newsletterOptIn ? 23 : 3,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.25s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }} />
              </button>
            </div>
            {newsletterSaved && <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#22c55e' }}>{newsletterOptIn ? 'Subscribed ✓' : 'Unsubscribed'}</p>}

            {/* Security */}
            <p className="settings-section-header" style={{ color: theme.accent, marginTop: 28, paddingTop: 20, borderTop: `1px solid rgba(255,255,255,0.08)` }}>{t('settings.sectionSecurity')}</p>
            <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.secondaryText }}>{t('settings.changePassword')}</p>
              {[
                { label: t('settings.currentPassword'), value: pwCurrent, set: setPwCurrent, id: 'pw-current' },
                { label: t('settings.newPassword'), value: pwNew, set: setPwNew, id: 'pw-new' },
                { label: t('settings.confirmPassword'), value: pwConfirm, set: setPwConfirm, id: 'pw-confirm' },
              ].map(({ label, value, set, id }) => (
                <label key={id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.secondaryText, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                  <input id={id} type="password" value={value} onChange={(e) => set(e.target.value)} autoComplete={id === 'pw-current' ? 'current-password' : 'new-password'}
                    style={{ padding: '11px 14px', background: `${theme.surface}cc`, border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 10, color: theme.text, fontSize: '0.88rem', outline: 'none' }} />
                </label>
              ))}
              {pwError && <p style={{ margin: 0, color: '#f87171', fontSize: '0.82rem' }}>{pwError}</p>}
              <button type="submit" disabled={pwSaving} style={{ padding: '11px', borderRadius: 10, border: 'none', background: pwSaved ? '#22c55e' : 'rgba(255,255,255,0.08)', color: pwSaved ? '#000' : theme.text, fontWeight: 700, fontSize: '0.85rem', cursor: pwSaving ? 'wait' : 'pointer', transition: 'background 0.3s' }}>
                {pwSaving ? t('settings.saving') : pwSaved ? t('settings.passwordUpdated') : t('settings.updatePassword')}
              </button>
            </form>
          </div>

          {/* Live avatar preview */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              overflow: 'hidden',
              border: `3px solid ${theme.accent}`,
              boxShadow: `0 0 0 4px ${theme.accent}33, 0 8px 24px rgba(0,0,0,0.5)`,
            }}>
              <img src={currentAvatarSrc} alt={settingsAvatar} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <p style={{ margin: 0, fontSize: '0.78rem', color: theme.secondaryText, textAlign: 'center', maxWidth: 110, lineHeight: 1.4 }}>
              {settingsName || data?.investor.name || 'Investor'}
            </p>
          </div>
        </div>

        {/* Account */}
        <p className="settings-section-header" style={{ color: theme.accent, marginTop: 28, paddingTop: 20, borderTop: `1px solid rgba(255,255,255,0.08)` }}>{t('settings.sectionAccount')}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          {[
            { label: t('account.username'), value: `@${data?.investor.username ?? '—'}` },
            { label: t('account.investment'), value: data ? formatMoney(data.investor.investmentAmount ?? 0, data.investor.currency || 'USD') : '—' },
            { label: t('account.profitRate'), value: data ? `${data.investor.profitPercentageOnInvestment ?? 0}%` : '—' },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: theme.surface, borderRadius: 12, padding: '12px 14px' }}>
              <p style={{ margin: 0, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: theme.secondaryText, fontWeight: 700 }}>{label}</p>
              <p style={{ margin: '4px 0 0', fontWeight: 700, fontSize: '0.95rem' }}>{value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="investor-loading" style={{ background: '#06131F' }}>
        <div className="investor-loading-inner">
          <div className="investor-loading-spinner" />
          <p>{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="investor-loading">
        <p>{error || t('error.unavailable')}</p>
        <Link to="/">{t('error.backToLogin')}</Link>
      </div>
    );
  }

  // Self-registered users with no assigned investments see the waiting room
  const isWaitingRoom =
    (data.investor.registrationMethod === 'email' || data.investor.registrationMethod === 'google') &&
    (!data.investor.assignedInvestmentIds || data.investor.assignedInvestmentIds.length === 0);

  const handleExplore = (panel: string) => {
    setPortalUnlocked(true);
    if (panel === 'journeys') dispatch(setActivePanel('journeys'));
    else if (panel === 'investments') dispatch(setActivePanel('open-investments'));
    else dispatch(setActivePanel('summary'));
  };

  if (isWaitingRoom) {
    const boardingDone = localStorage.getItem('nomadme_boarding_completed');
    if (!boardingDone) {
      return <BoardingScreen investorName={data.investor.displayName || data.investor.name} />;
    }
    const waitingRoomSeen = localStorage.getItem('nomadme_waitingroom_seen');
    if (!waitingRoomSeen && !portalUnlocked) {
      // First-ever visit after boarding — show WaitingRoom once, mark it done
      localStorage.setItem('nomadme_waitingroom_seen', 'true');
      return <WaitingRoom investor={data.investor} onLogout={handleLogout} onExplore={handleExplore} />;
    }
    // WaitingRoom already seen or user clicked Explore → fall through to full portal
  }

  const avatarBadgeMap: Record<string, string> = { ...LEGACY_AVATAR_MAP };
  for (const av of avatarOptions) avatarBadgeMap[av._id] = av.imageUrl;
  const avatarBadgeSrc = data.investor.avatar ? (avatarBadgeMap[data.investor.avatar] ?? null) : null;

  const isNonInvestor = !data.investor.assignedInvestmentIds || data.investor.assignedInvestmentIds.length === 0;
  const displayNameStr = data.investor.displayName || data.investor.name || '';
  const firstName = displayNameStr.split(' ')[0] || displayNameStr;

  return (
    <main className={`investor-dashboard-shell gamified-shell${viewMode === 'globe' ? ' gamified-shell--globe' : ''}`} style={{ background: theme.background, color: theme.text }}>
      <div className="investor-topbar gamified-topbar">
        <div className="topbar-identity">
          <img src="/logos/nomadme_sourcing_trading_mark.png" className="topbar-brand-logo" alt="nomadme" />
          <div>
            <p className="mini-label">{isNonInvestor ? `${firstName}'s NomadMee` : t('topbar.portal')}</p>
            <h1>{isNonInvestor ? 'My NomadMee' : t('topbar.welcome', { name: displayNameStr })}</h1>
            <p className="mini-description">{t('topbar.subtitle')}</p>
          </div>
        </div>
        <div className="topbar-controls">
          {/* View toggle */}
          <div className="view-mode-toggle">
            <button
              type="button"
              className={`view-mode-btn${viewMode === 'me' ? ' view-mode-btn--active' : ''}`}
              onClick={() => setViewMode('me')}
            >
              {t('topbar.me')}
            </button>
            <button
              type="button"
              className={`view-mode-btn${viewMode === 'globe' ? ' view-mode-btn--active' : ''}`}
              onClick={() => setViewMode('globe')}
            >
              {t('topbar.globe')}
            </button>
          </div>
          <CurrencySwitcher variant="ghost" accentColor={theme.accent} />
          <LanguageSwitcher variant="ghost" accentColor={theme.accent} />
          {/* Mobile-only: compact globe toggle */}
          <button
            type="button"
            className="mobile-globe-btn"
            onClick={() => setViewMode(viewMode === 'globe' ? 'me' : 'globe')}
            style={{ background: viewMode === 'globe' ? `${theme.accent}22` : 'rgba(255,255,255,0.06)', color: viewMode === 'globe' ? theme.accent : theme.secondaryText, borderColor: viewMode === 'globe' ? `${theme.accent}55` : 'rgba(255,255,255,0.12)' }}
          >
            🌍
          </button>
          {avatarBadgeSrc ? (
            <div
              className="topbar-avatar-badge"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 12px',
                borderRadius: '999px',
                background: 'rgba(255,255,255,0.08)',
                border: `1px solid ${theme.accentSoft}`,
              }}
            >
              <img
                src={avatarBadgeSrc}
                alt={data.investor.avatar}
                style={{ width: '28px', height: '28px', borderRadius: '999px', objectFit: 'cover' }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '0.68rem', color: theme.secondaryText, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t('topbar.investor')}</span>
                <strong style={{ fontSize: '0.8rem', color: theme.text }}>{data.investor.displayName || data.investor.name}</strong>
              </div>
            </div>
          ) : null}
          <button type="button" className="logout-button" onClick={handleLogout} style={{ background: theme.accent, color: theme.background }}>
            {t('topbar.logout')}
          </button>
        </div>
      </div>

      {viewMode === 'globe' && (
        <div className="globe-view-wrap" style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <WorldMap accentColor={theme.accent} />
        </div>
      )}

      {viewMode === 'me' && <div className="gamified-grid">
        <aside className="dashboard-sidebar" style={{ background: theme.surface }}>
          <div className="sidebar-section">
            <h2>{t('sidebar.explore')}</h2>
            <div className="dashboard-tabs">

              {/* ── Investor group ── */}
              <button
                type="button"
                className={`sidebar-group-header${activeGroup === 'investor' ? ' sidebar-group-header--active' : ''}`}
                style={activeGroup === 'investor'
                  ? { color: theme.accent, borderColor: `${theme.accent}44`, background: `${theme.accent}0f` }
                  : { color: theme.text, borderColor: 'transparent' }
                }
                onClick={() => handleGroupClick('investor')}
              >
                <FiPackage size={15} />
                <span>{isNonInvestor ? 'Portfolio' : t('nav.investor', 'Investor')}</span>
                <span className="sidebar-group-chevron" style={{ color: activeGroup === 'investor' ? theme.accent : theme.secondaryText }}>
                  {activeGroup === 'investor' ? '▾' : '▸'}
                </span>
              </button>
              {activeGroup === 'investor' && (
                <div className="sidebar-sub-nav">
                  {([
                    { id: 'summary' as PanelId, labelKey: 'nav.summary', Icon: FiHome },
                    { id: 'cargos'  as PanelId, labelKey: 'nav.cargos',  Icon: FiPackage },
                    { id: 'map'     as PanelId, labelKey: 'nav.map',     Icon: FiMap },
                  ]).map((sub) => (
                    <button
                      key={sub.id}
                      type="button"
                      className={`sidebar-sub-btn${sub.id === activePanel ? ' sidebar-sub-btn--active' : ''}`}
                      style={sub.id === activePanel
                        ? { color: theme.background, background: theme.accent, borderColor: theme.accent }
                        : { color: theme.text, borderColor: 'transparent' }
                      }
                      onClick={() => handlePanelChange(sub.id)}
                    >
                      <sub.Icon size={13} />
                      {t(sub.labelKey)}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Shop group ── */}
              <button
                type="button"
                className={`sidebar-group-header${activeGroup === 'shop' ? ' sidebar-group-header--active' : ''}`}
                style={activeGroup === 'shop'
                  ? { color: theme.accent, borderColor: `${theme.accent}44`, background: `${theme.accent}0f` }
                  : { color: theme.text, borderColor: 'transparent' }
                }
                onClick={() => handleGroupClick('shop')}
              >
                <FiShoppingBag size={15} />
                <span>{t('nav.shop')}</span>
                {cartItems.length > 0 && (
                  <span className="sidebar-cart-badge" style={{ background: theme.accent, color: theme.background }}>
                    {cartItems.reduce((s, i) => s + i.quantity, 0)}
                  </span>
                )}
                <span className="sidebar-group-chevron" style={{ color: activeGroup === 'shop' ? theme.accent : theme.secondaryText }}>
                  {activeGroup === 'shop' ? '▾' : '▸'}
                </span>
              </button>
              {activeGroup === 'shop' && (
                <div className="sidebar-sub-nav">
                  {([
                    { id: 'shop'          as PanelId, label: 'Browse',  Icon: FiShoppingBag },
                    { id: 'cart'          as PanelId, label: 'Cart',    Icon: FiShoppingBag, badge: cartItems.reduce((s, i) => s + i.quantity, 0) },
                    { id: 'order-history' as PanelId, label: 'History', Icon: FiHome },
                  ]).map((sub) => (
                    <button
                      key={sub.id}
                      type="button"
                      className={`sidebar-sub-btn${sub.id === activePanel ? ' sidebar-sub-btn--active' : ''}`}
                      style={sub.id === activePanel
                        ? { color: theme.background, background: theme.accent, borderColor: theme.accent }
                        : { color: theme.text, borderColor: 'transparent' }
                      }
                      onClick={() => handlePanelChange(sub.id)}
                    >
                      <sub.Icon size={13} />
                      {sub.label}
                      {sub.badge && sub.badge > 0 && sub.id !== activePanel && (
                        <span className="sidebar-cart-badge" style={{ background: theme.accent, color: theme.background }}>{sub.badge}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* ── Solo items ── */}
              {([
                { id: 'journeys' as PanelId, labelKey: 'nav.journeys', Icon: FiCompass },
                { id: 'support'  as PanelId, labelKey: 'nav.support',  Icon: FiHeadphones },
                { id: 'settings' as PanelId, labelKey: 'nav.settings', Icon: FiSettings },
              ]).map((solo) => (
                <button
                  key={solo.id}
                  type="button"
                  className={`dashboard-tab${solo.id === activePanel ? ' dashboard-tab-active' : ''}`}
                  onClick={() => handlePanelChange(solo.id)}
                  style={{
                    borderColor: solo.id === activePanel ? theme.accent : 'transparent',
                    color: solo.id === activePanel ? theme.background : theme.text,
                    background: solo.id === activePanel ? theme.accent : 'transparent',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}
                >
                  <solo.Icon size={15} />
                  {t(solo.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-note" style={{ borderColor: theme.accentSoft }}>
            <p>{t('sidebar.note')}</p>
          </div>
        </aside>

        <section className="dashboard-content">
          {activePanel === 'summary' && renderSummary()}
          {activePanel === 'cargos' && renderCargos()}
          {activePanel === 'map' && renderMap()}
          {activePanel === 'shop' && renderShop()}
          {activePanel === 'cart' && renderCart()}
          {activePanel === 'order-history' && renderOrderHistory()}
          {activePanel === 'journeys' && renderJourneys()}
          {activePanel === 'open-investments' && renderOpenInvestments()}
          {activePanel === 'support' && renderSupport()}
          {activePanel === 'settings' && renderSettings()}
        </section>
      </div>}

      {/* ── Floating theme toggle ── */}
      <div className="theme-fab-wrap">
        {themePickerOpen && (
          <div className="theme-fab-panel" style={{ background: theme.surface, boxShadow: `0 8px 32px ${theme.panelGlow}` }}>
            {dashboardThemes.map((themeOption, index) => (
              <button
                key={index}
                type="button"
                aria-label={themeOption.name}
                title={`${themeOption.emoji} ${themeOption.name}`}
                className={`theme-chip${activeTheme === index ? ' theme-chip-active' : ''}`}
                style={{
                  background: `conic-gradient(${themeOption.background} 0deg 180deg, ${themeOption.accent} 180deg 360deg)`,
                  boxShadow: activeTheme === index ? `0 0 0 2px #fff, 0 0 0 4px ${themeOption.accent}88` : 'none',
                }}
                onClick={() => handleThemeChange(index)}
              />
            ))}
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="theme-fab"
            style={{ background: theme.surface, boxShadow: `0 4px 16px ${theme.panelGlow}`, border: `2px solid ${theme.accent}55` }}
            aria-label="Personalise theme"
            title="Personalise theme"
            onClick={() => {
              setThemePickerOpen((o) => !o);
              try { localStorage.setItem('nomadme_theme_fab_seen', '1'); } catch {}
            }}
          >
            <span style={{ fontSize: 22 }}>{dashboardThemes[activeTheme]?.emoji}</span>
          </button>
          {/* Tooltip */}
          <div style={{
            position: 'absolute',
            bottom: '110%',
            right: 0,
            background: 'rgba(10,12,20,0.92)',
            color: '#f1f5f9',
            fontSize: '0.72rem',
            fontWeight: 700,
            padding: '5px 10px',
            borderRadius: 7,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            opacity: themePickerOpen ? 0 : 1,
            transform: themePickerOpen ? 'translateY(4px)' : 'translateY(0)',
            transition: 'opacity 0.2s, transform 0.2s',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            Personalise theme
            <span style={{ position: 'absolute', bottom: -5, right: 14, width: 8, height: 8, background: 'rgba(10,12,20,0.92)', borderRight: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', transform: 'rotate(45deg)' }} />
          </div>
        </div>
      </div>

      {/* ── Mobile bottom navigation bar (hidden on desktop) ── */}
      <nav
        className="mobile-bottom-nav"
        style={{ background: `${theme.surface}f4`, borderTopColor: `rgba(255,255,255,0.07)` }}
      >
        {mobileNavEntries.map((entry) => {
          const isActive = viewMode === 'me' && (entry.group ? activeGroup === entry.group : activePanel === entry.panel);
          const cartBadge = entry.panel === 'shop' && cartItems.length > 0
            ? cartItems.reduce((s, i) => s + i.quantity, 0)
            : 0;
          return (
            <button
              key={entry.panel}
              type="button"
              className={`mobile-nav-btn${isActive ? ' mobile-nav-btn--active' : ''}`}
              style={{ color: isActive ? theme.accent : theme.secondaryText, position: 'relative' }}
              onClick={() => { setViewMode('me'); if (entry.group) handleGroupClick(entry.group); else handlePanelChange(entry.panel); }}
            >
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <entry.Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                {cartBadge > 0 && (
                  <span style={{ position: 'absolute', top: -6, right: -8, background: theme.accent, color: theme.background, borderRadius: '999px', fontSize: '0.55rem', fontWeight: 800, padding: '1px 4px', lineHeight: 1.4 }}>
                    {cartBadge}
                  </span>
                )}
              </div>
              <span>{t(entry.labelKey, entry.labelKey.split('.').pop() ?? entry.labelKey)}</span>
            </button>
          );
        })}
        <button
          type="button"
          className={`mobile-nav-btn${viewMode === 'globe' ? ' mobile-nav-btn--active' : ''}`}
          style={{ color: viewMode === 'globe' ? theme.accent : theme.secondaryText }}
          onClick={() => setViewMode(viewMode === 'globe' ? 'me' : 'globe')}
        >
          <span style={{ fontSize: '20px', lineHeight: 1 }}>🌍</span>
          <span>{t('nav.globe')}</span>
        </button>
      </nav>
    </main>
  );
};

export default InvestorHome;
